import React, { useState, useEffect, useMemo } from 'react';
import { Box, Card, IconButton, Tooltip, Checkbox, Typography } from '@mui/material';
import { DatePicker, Input, Dropdown } from 'antd';
import { Mail, MailOpen, Trash2, Calendar, Search, MoreVertical } from 'lucide-react';
import SubHeader from '../SubHeader/SubHeader';
import PremiumModuleTable from '../../../pages/Task/Components/common/PremiumModuleTable';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { getSession } from '../../../getSession';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import classnames from 'classnames';

const { RangePicker } = DatePicker;

const Notification = () => {
  const { userId } = getSession();
  const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
  const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchText, setSearchText] = useState('');
  
  // Date range state (default to last 7 days to today)
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);

  const [rowSelection, setRowSelection] = useState({});

  const fetchData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const payload = {
        Token: "-2295521862261168",
        Details: {
          UserId: parseInt(userId, 10),
          Start: (currentPage - 1) * itemsPerPage,
          Limit: itemsPerPage
        }
      };

      const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.NOTIFICATION_DETAILS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to fetch notifications');
      
      const resp = await response.json();
      let raw = resp?.d ? (typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d) : (resp?.Details || resp);
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch (e) {}
      }
      
      let arr = [];
      
      // Look for the notifications array in multiple possible places
      if (Array.isArray(raw) && raw.length > 0) {
        if (raw[0].notifications) {
          arr = typeof raw[0].notifications === 'string' ? JSON.parse(raw[0].notifications) : raw[0].notifications;
          setTotalRecords(raw[0].TotalRecords || arr.length);
        } else {
          arr = raw;
          setTotalRecords(raw.length);
        }
      } else if (raw && raw.notifications) {
        arr = typeof raw.notifications === 'string' ? JSON.parse(raw.notifications) : raw.notifications;
        setTotalRecords(raw.TotalRecords || arr.length);
      } else if (raw && raw.data) {
        arr = raw.data;
        setTotalRecords(raw.recordsFiltered || raw.recordsTotal || arr.length);
      } else if (raw && Array.isArray(raw.Data)) {
        arr = raw.Data;
        setTotalRecords(raw.TotalRecords || arr.length);
      } else if (Array.isArray(raw)) {
        arr = raw;
        setTotalRecords(raw.length);
      }
      
      const mapped = arr.map((it, idx) => ({
        id: it.NotificationId || it.Id || it.ID || it.id || it.NFHID || idx,
        title: it.Title || it.NotificationTitle || it.Event || it.Subject || 'Notification',
        message: it.Message || it.Description || it.Body || it.Msg || it.NotificationText || it.content || '',
        time: it.Time || it.CreatedOn || it.CreatedDate || it.Date || it.TimeStamp || it.createdon || '',
        isRead: (it.IsRead === 1 || it.IsRead === true || it.Read === true || String(it.IsRead) === '1') ? true : false,
        raw: it
      }));

      setData(mapped);
    } catch (error) {
      console.error('Fetch notifications error:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId, currentPage, itemsPerPage, dateRange]);

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentPage === 1) {
        fetchData();
      } else {
        setCurrentPage(1); // Changing page will trigger fetch
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  const handleMarkAsRead = async (id) => {
    try {
      const response = await fetch(`${WCF_BASE}/UserCRMCampaign/Service/Notification.asmx/FuncToMarkAsRead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strNFHID: String(id) })
      });
      if (response.ok) {
        setData(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
        toast.success('Marked as read');
      }
    } catch (error) {
      console.error('markAsRead error:', error);
      toast.error('Error marking as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${WCF_BASE}/UserCRMCampaign/Service/Notification.asmx/FuncToMarkAsDeleted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strNFHID: String(id) })
      });
      if (response.ok) {
        setData(prev => prev.filter(n => n.id !== id));
        setTotalRecords(prev => Math.max(0, prev - 1));
        toast.success('Notification deleted');
      }
    } catch (error) {
      console.error('deleteNotification error:', error);
      toast.error('Error deleting notification');
    }
  };

  const handleMarkAllAsRead = async () => {
    const selectedIds = Object.keys(rowSelection).filter(key => rowSelection[key]);
    if (selectedIds.length === 0) {
      toast.error("Please select at least one notification");
      return;
    }
    
    let successCount = 0;
    for (const rowIdx of selectedIds) {
      const n = data[rowIdx];
      if (n && !n.isRead) {
        try {
          await fetch(`${WCF_BASE}/UserCRMCampaign/Service/Notification.asmx/FuncToMarkAsRead`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ strNFHID: String(n.id) })
          });
          successCount++;
        } catch (err) {}
      }
    }
    
    if (successCount > 0) {
      toast.success(`Marked ${successCount} notifications as read`);
      fetchData(); // Refresh list to get accurate state
      setRowSelection({});
    } else {
      toast.info("Selected notifications are already read");
    }
  };

  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          size="small"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          size="small"
        />
      ),
      size: 50,
    },
    {
      id: 'notification',
      header: 'Notification Details',
      cell: ({ row }) => {
        const n = row.original;
        return (
          <Box className="flex items-start gap-4 py-2 w-full group relative">
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              !n.isRead ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              {!n.isRead ? (
                <Mail className="w-5 h-5 text-blue-600" />
              ) : (
                <MailOpen className="w-5 h-5 text-gray-500" />
              )}
            </div>
            <Box className="flex-1 min-w-0 pr-12">
              <div className="flex items-center gap-2 mb-1">
                <Typography variant="subtitle2" className="font-semibold text-gray-900 truncate">
                  {n.title}
                </Typography>
                {!n.isRead && (
                  <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0"></span>
                )}
              </div>
              <Typography variant="body2" className="text-gray-600 mb-1">
                {n.message}
              </Typography>
              <Typography variant="caption" className="text-gray-400">
                {n.time}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      id: 'actions',
      header: 'Action',
      size: 80,
      cell: ({ row }) => {
        const n = row.original;
        const items = [];
        if (!n.isRead) {
          items.push({
            key: 'read',
            label: 'Mark as read',
            icon: <MailOpen className="w-4 h-4" />,
            onClick: () => handleMarkAsRead(n.id)
          });
        }
        items.push({
          key: 'delete',
          label: 'Delete',
          danger: true,
          icon: <Trash2 className="w-4 h-4" />,
          onClick: () => handleDelete(n.id)
        });

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <IconButton size="small">
              <MoreVertical className="w-5 h-5 text-gray-500" />
            </IconButton>
          </Dropdown>
        );
      }
    }
  ], []);

  return (
    <Box className="bg-gray-50 min-h-screen">
      <SubHeader
        title="Notifications"
        searchQuery={searchText}
        setSearchQuery={setSearchText}
        searchPlaceholder="Type to search notifications"
        totalCount={totalRecords}
        showViewSwitcher={false}
        showAdd={false}
        showFilter={false}
      >
        {Object.keys(rowSelection).filter(k => rowSelection[k]).length > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-md transition border border-transparent hover:border-blue-200 flex items-center gap-2 mr-3"
          >
            <MailOpen className="w-4 h-4" />
            Mark selected as read
          </button>
        )}
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          format="DD-MMM-YYYY"
          className="rounded-md border-gray-200 shadow-sm"
          allowClear={false}
        />
      </SubHeader>

      <Box className="p-4 md:p-6">
        <Card className="shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <PremiumModuleTable
          columns={columns}
          data={data}
          totalRecords={totalRecords}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
          }}
          rowSelection={rowSelection}
          onManualRowSelectionChange={setRowSelection}
          loading={loading}
        />
      </Card>
      </Box>
    </Box>
  );
};

export default Notification;
